import { KnowledgeEditor } from '@/components/knowledge/KnowledgeEditor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDataset, useDatasetsList } from '@/hooks/useDatasets';
import { useCreateDiscovery } from '@/hooks/useDiscoveries';
import { useCreateKnowledge, useKnowledge, usePatchKnowledge } from '@/hooks/useKnowledge';
import { extractApiError } from '@/lib/api';
import { friendlyError } from '@/lib/error-messages';
import type { SuggestedEdge } from '@/lib/discoveries';
import { type KnowledgePayload, type KnowledgeObject, deriveKinds } from '@/lib/knowledge';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

// Hand-off from /discoveries/:id → /knowledge/new via react-router state.
// "Customize first" on the SuggestedKnowledgeCard passes the suggestion in
// pre-formatted shape so the user only has to tweak (not re-type) before
// saving. When `rerunDiscovery` is present we replace the "navigate back to
// /knowledge on save" UX with "kick off the rerun + navigate to the new
// discovery" so the round-trip stays a one-click flow.
//
// v2: the hand-off carries `edges_required` and `edges_forbidden` in
// addition to tiers. KnowledgeEditor already renders all three sections,
// so we just forward the suggestion into `payload` and let the user edit
// any of them before saving.
interface PrefillState {
  prefill?: {
    datasetId: string;
    name: string;
    tiers: string[][];
    tierLabels?: string[];
    edges_required?: SuggestedEdge[];
    edges_forbidden?: SuggestedEdge[];
    rerunDiscovery?: {
      dataset_id: string;
      seed: number;
    };
  };
}

// "Org-wide" sentinel for the dataset Select — Radix Select cannot store an
// empty string as a value (it throws on render), so we map "" ↔ this token
// at the form boundary.
const ORG_WIDE = '__org_wide__';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200, 'Max 200 characters'),
  // Empty string in form state → null on submit. Radix Select uses ORG_WIDE
  // sentinel; the resolver maps it back to '' before zod sees it.
  datasetId: z.string().max(30),
});

type FormValues = z.infer<typeof formSchema>;

const EMPTY_PAYLOAD: KnowledgePayload = {
  edges_forbidden: [],
  edges_required: [],
  tiers: [],
};

export default function KnowledgeNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = !!editId;
  const [searchParams] = useSearchParams();
  const preselectedDataset = searchParams.get('dataset_id') ?? '';

  // Captured once on mount — react-router preserves location.state across
  // re-renders but we only ever want to consume it the first time.
  const prefill = React.useMemo(
    () => (location.state as PrefillState | null)?.prefill,
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally read once on mount
    [],
  );
  // Keep a ref to the rerun directive so the create onSuccess handler (which
  // runs outside the React render) sees the latest value without depending
  // on closure re-creation.
  const rerunRef = React.useRef(prefill?.rerunDiscovery);
  rerunRef.current = prefill?.rerunDiscovery;

  // In edit mode load the existing object to pre-fill the form. The list
  // page seeds the item cache via setQueryData, so this is usually a hit.
  const existingQuery = useKnowledge(editId);
  const existing = existingQuery.data;

  const datasetsQuery = useDatasetsList({ limit: 100 });
  const [payload, setPayload] = React.useState<KnowledgePayload>(EMPTY_PAYLOAD);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: prefill && !isEdit ? prefill.name : '',
      datasetId:
        prefill && !isEdit
          ? prefill.datasetId || ORG_WIDE
          : preselectedDataset || ORG_WIDE,
    },
  });

  // Pre-fill once the existing object lands. Guard with a ref so user edits
  // aren't clobbered by a background refetch.
  const hydrated = React.useRef(false);
  React.useEffect(() => {
    if (!isEdit || !existing || hydrated.current) return;
    hydrated.current = true;
    form.reset({
      name: existing.name,
      datasetId: existing.dataset_id ?? ORG_WIDE,
    });
    setPayload({
      edges_forbidden: existing.payload?.edges_forbidden ?? [],
      edges_required: existing.payload?.edges_required ?? [],
      tiers: existing.payload?.tiers ?? [],
    });
  }, [isEdit, existing, form]);

  // Prefill (create-mode) from /discoveries/:id "Customize first" hand-off.
  // Separate ref from `hydrated` so the two flows can't accidentally
  // disable each other if the user opens this page via both paths.
  // tierLabels are intentionally ignored — KnowledgeEditor doesn't expose
  // per-tier labels today, per the spec we silently drop them.
  //
  // v2: edges_required + edges_forbidden flow through from the suggestion
  // card. They arrive as {src,dst} objects; convert to the payload's
  // [string, string] tuple shape at the boundary.
  const prefilled = React.useRef(false);
  React.useEffect(() => {
    if (isEdit || !prefill || prefilled.current) return;
    prefilled.current = true;
    setPayload({
      edges_forbidden: (prefill.edges_forbidden ?? []).map(
        (e) => [e.src, e.dst] as [string, string],
      ),
      edges_required: (prefill.edges_required ?? []).map(
        (e) => [e.src, e.dst] as [string, string],
      ),
      tiers: prefill.tiers,
    });
  }, [isEdit, prefill]);

  const selectedDatasetId = form.watch('datasetId') === ORG_WIDE ? '' : form.watch('datasetId');
  // For org-wide knowledge there's no source-of-truth column list. The
  // editor falls back to a disabled state; the user picks a dataset to
  // populate the dropdowns.
  const datasetQuery = useDataset(selectedDatasetId || undefined);
  const columns = datasetQuery.data?.column_names ?? [];

  const createDiscovery = useCreateDiscovery();

  async function rerunAfterKnowledge(ko: KnowledgeObject): Promise<void> {
    const rerun = rerunRef.current;
    if (!rerun) {
      toast.success('Knowledge created.');
      navigate('/knowledge', { replace: true });
      return;
    }
    try {
      const job = await createDiscovery.mutateAsync({
        body: {
          dataset_id: rerun.dataset_id,
          knowledge_id: ko.id,
          params: { seed: rerun.seed },
        },
      });
      toast.success('Re-running with your knowledge — opening the new discovery.');
      navigate(`/discoveries/${job.id}`, { replace: true });
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'quota.exhausted' || apiErr?.code === 'quota.exceeded') {
        toast.error(
          'Your free quota is used up. The knowledge was saved — open Knowledge to re-run later, or upgrade your plan.',
        );
        navigate(`/knowledge/${ko.id}`, { replace: true });
        return;
      }
      if (apiErr?.code === 'idempotency.replay') {
        const existingId =
          (apiErr.metadata?.existing_id as string | undefined) ??
          (apiErr.metadata?.discovery_id as string | undefined);
        if (existingId) {
          toast.success('Re-running with your knowledge — opening the new discovery.');
          navigate(`/discoveries/${existingId}`, { replace: true });
          return;
        }
      }
      toast.error(
        `Saved the knowledge, but couldn't start the re-run. ${friendlyError(
          apiErr?.code,
          apiErr?.message ?? 'Unexpected error.',
        )}`,
      );
      toast.message('Open Knowledge to retry', {
        action: {
          label: 'Open Knowledge →',
          onClick: () => navigate(`/knowledge/${ko.id}`),
        },
      });
    }
  }

  const create = useCreateKnowledge({
    onSuccess: (ko) => {
      void rerunAfterKnowledge(ko);
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Failed to create.'));
    },
  });

  const patch = usePatchKnowledge({
    onSuccess: () => {
      toast.success('Knowledge updated.');
      navigate('/knowledge', { replace: true });
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Failed to update.'));
    },
  });

  function submit(values: FormValues) {
    const kinds = deriveKinds(payload);
    if (kinds.length === 0) {
      toast.error('Add at least one edge or tier before saving.');
      return;
    }
    // Sanity-check no incomplete edges leak through. Server also rejects
    // self-loops + empty endpoints; we surface the friendlier message
    // BEFORE the round-trip.
    const allEdges = [...payload.edges_forbidden, ...payload.edges_required];
    if (allEdges.some(([a, b]) => !a || !b)) {
      toast.error('Every edge needs both a source and a target.');
      return;
    }
    if (allEdges.some(([a, b]) => a === b)) {
      toast.error('Self-loops are not allowed.');
      return;
    }

    if (isEdit && editId) {
      // dataset_id is immutable on patch — backend recomputes kinds. We only
      // send the editable fields: name + payload.
      patch.mutate({ id: editId, body: { name: values.name.trim(), payload } });
      return;
    }

    create.mutate({
      name: values.name.trim(),
      dataset_id: values.datasetId === ORG_WIDE ? null : values.datasetId,
      kinds,
      payload,
    });
  }

  const isPending = create.isPending || patch.isPending || createDiscovery.isPending;

  if (isEdit && existingQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading knowledge…
      </div>
    );
  }

  if (isEdit && existingQuery.isError) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
          Couldn't load this knowledge object — it may have been deleted.
        </div>
        <Button variant="outline" onClick={() => navigate('/knowledge')}>
          Back to knowledge
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit knowledge' : 'New knowledge'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Encode what you already know about your variables — the algorithm uses these constraints
          to refine the graph.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. Basics</CardTitle>
              <CardDescription>
                {isEdit
                  ? 'Rename this set of constraints. The pinned dataset cannot be changed.'
                  : 'Name this set of constraints and (optionally) pin it to a dataset.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. SACHS biology priors"
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="datasetId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dataset</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value || ORG_WIDE}
                        onValueChange={field.onChange}
                        disabled={isPending || isEdit}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a dataset" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ORG_WIDE}>Org-wide (any dataset)</SelectItem>
                          {(datasetsQuery.data?.items ?? []).map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name} ({d.n_rows.toLocaleString()} × {d.n_cols})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Org-wide constraints apply to any dataset whose columns match the variable
                      names below.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <KnowledgeEditor
            columns={columns}
            value={payload}
            onChange={setPayload}
            disabled={isPending}
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/knowledge')}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save knowledge'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
